import './App.css';
import { Recorder } from 'react-record-rtc';
import CodeBlock from './docs/CodeBlock';
import PropsTable from './docs/PropsTable';
import {
  recorderProps,
  useCameraStreamReturns,
  useVideoRecorderReturns,
  usePhotoCaptureReturns,
} from './docs/content';

const REPO_URL = 'https://github.com/PotLid/react-recorder';
const NPM_URL = 'https://www.npmjs.com/package/react-record-rtc';

const installSnippet = `npm install react-record-rtc`;

const quickStartSnippet = `import { Recorder } from 'react-record-rtc';

function App() {
  return (
    <Recorder
      initialFacing="user"
      onRecordingComplete={(blob, url) => console.log('recorded', blob, url)}
      onPhotoCapture={(blob, url) => console.log('photo', blob, url)}
      onPermissionChange={(permission) => console.log('permission', permission)}
    />
  );
}`;

const fullRecordRtcSnippet = `const { recorder, recordingState, pauseRecording, resumeRecording } =
  useVideoRecorder(camera, videoRef, {
    recordRTCOptions: {
      timeSlice: 1000,
      ondataavailable: (chunk) => uploadChunk(chunk),
    },
  });

// anything not wrapped above is still reachable on the live instance
recorder?.setRecordingDuration(30_000).onRecordingStopped(() => {
  console.log('auto-stopped');
});`;

const headlessSnippet = `import { useRef } from 'react';
import { useCameraStream, useVideoRecorder, usePhotoCapture } from 'react-record-rtc';

function CustomRecorder() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { camera, permission, isLoading } = useCameraStream(videoRef);
  const { isRecording, isPaused, startRecording, pauseRecording, resumeRecording, stopRecording } =
    useVideoRecorder(camera, videoRef);
  const { photoURL, takePhoto } = usePhotoCapture(videoRef);

  return <video ref={videoRef} autoPlay playsInline />;
}`;

function App() {
  return (
    <div className="App">
      <nav className="site-nav">
        <a className="site-nav-brand" href="#top">react-record-rtc</a>
        <div className="site-nav-links">
          <a href="#demo">Demo</a>
          <a href="#quick-start">Quick Start</a>
          <a href="#api">API</a>
          <a href={REPO_URL} target="_blank" rel="noopener noreferrer">GitHub</a>
        </div>
      </nav>

      <header className="hero" id="top">
        <div className="container">
          <h1>react-record-rtc</h1>
          <p>
            A React component and hooks for in-browser camera video recording and photo capture,
            built on <a href="https://recordrtc.org/" target="_blank" rel="noopener noreferrer">RecordRTC</a>.
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary" href="#quick-start">Get started</a>
            <a className="btn btn-secondary" href={NPM_URL} target="_blank" rel="noopener noreferrer">View on npm</a>
            <a className="btn btn-secondary" href={REPO_URL} target="_blank" rel="noopener noreferrer">View on GitHub</a>
          </div>
        </div>
      </header>

      <section className="section" id="demo">
        <div className="container">
          <h2>Live demo</h2>
          <p className="section-intro">
            This is the actual <code>&lt;Recorder /&gt;</code> component from the published package,
            running unmodified. It'll ask for camera/microphone permission on load.
          </p>
          <div className="demo-panel">
            <div className="demo-panel-recorder">
              <Recorder />
            </div>
            <div className="demo-note">
              Requires a secure context (HTTPS or localhost) — this page qualifies since it's served
              over GitHub Pages.
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="quick-start">
        <div className="container">
          <h2>Installation</h2>
          <p className="section-intro">
            <code>react</code> and <code>react-dom</code> (&gt;=17) are peer dependencies — install
            them in your app if you haven't already.
          </p>
          <CodeBlock code={installSnippet} language="sh" />

          <h2 style={{ marginTop: 40 }}>Quick start</h2>
          <p className="section-intro">
            Drop the component in as-is — it renders its own camera preview, record/pause/resume/stop/retry
            controls, camera-flip buttons (when more than one camera is available), and a take-photo button.
          </p>
          <CodeBlock code={quickStartSnippet} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Full RecordRTC access</h2>
          <p className="section-intro">
            <code>useVideoRecorder</code> is a thin wrapper, not a fixed subset — nothing RecordRTC
            offers is out of reach. <code>recordRTCOptions</code> is passed straight through to{' '}
            <code>new RecordRTC(stream, options)</code>, so <code>mimeType</code>,{' '}
            <code>videoBitsPerSecond</code>, <code>timeSlice</code> + <code>ondataavailable</code>{' '}
            (chunked/streaming recording), <code>disableLogs</code>, etc. all just work.
            <code>pauseRecording</code>/<code>resumeRecording</code>/<code>reset</code> are wrapped
            directly, and the raw RecordRTC instance is returned as <code>recorder</code> while a
            recording is active/paused, so anything not wrapped here is still one call away.
          </p>
          <CodeBlock code={fullRecordRtcSnippet} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2>Headless hooks</h2>
          <p className="section-intro">
            For a fully custom UI, use the hooks directly against your own <code>&lt;video&gt;</code> ref.
          </p>
          <CodeBlock code={headlessSnippet} />
        </div>
      </section>

      <section className="section" id="api">
        <div className="container">
          <h2>API reference</h2>
          <p className="section-intro">Complete prop and return-value reference for the component and every hook.</p>

          <h3>&lt;Recorder /&gt; props</h3>
          <PropsTable rows={recorderProps} />

          <h3>useCameraStream(videoRef, initialFacing?, onPermissionChange?)</h3>
          <PropsTable rows={useCameraStreamReturns} />

          <h3>useVideoRecorder(camera, videoRef, options?)</h3>
          <PropsTable rows={useVideoRecorderReturns} />

          <h3>usePhotoCapture(videoRef, onPhotoCapture?)</h3>
          <PropsTable rows={usePhotoCaptureReturns} />
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          MIT Licensed · <a href={REPO_URL} target="_blank" rel="noopener noreferrer">Source on GitHub</a> ·{' '}
          <a href={NPM_URL} target="_blank" rel="noopener noreferrer">Package on npm</a>
        </div>
      </footer>
    </div>
  );
}

export default App;
